import React, { useLayoutEffect, useMemo, useState } from 'react';

import { useEventCallback } from '@spazious/react-hooks';

import { DraggableLabelEventHandler } from './types.js';

export interface DraggableLabelHookOptions {
  value: number;
  onDrag?: DraggableLabelEventHandler;
  defaultValue?: number;
  step?: string | number;
  min?: string | number;
  max?: string | number;
  disabled?: boolean;
  axis?: 'x' | 'y';
  stopPropagation?: boolean;
}

export const useDraggableLabel = <TElement = Element>(opts: DraggableLabelHookOptions) => {
  const {
    value,
    onDrag,
    step = 0.01,
    min = -Infinity,
    max = Infinity,
    disabled,
    axis = 'y',
    stopPropagation = true,
  } = opts;

  const parserdStep = parseFloat(step.toString());
  const parsedMin = parseFloat(min.toString());
  const parsedMax = parseFloat(max.toString());
  const decimals = useMemo(() => step.toString().split('.')[1]?.length ?? 0, [step]);
  const [dragging, setDragging] = useState(false);
  const [snapshot, setSnapshot] = useState(value);
  const [startVal, setStartVal] = useState(0);
  const axisVar = `client${axis.toUpperCase() as 'Y' | 'X'}` as const;

  const onUpdate = useEventCallback((evt: PointerEvent) => {
    if (stopPropagation) {
      evt.stopPropagation();
    }
    if (startVal) {
      let value = snapshot - (evt[axisVar] - startVal) * parserdStep;

      if (!Number.isFinite(value)) {
        value = Number.isFinite(parsedMax) ? parsedMax : Number.isFinite(parsedMin) ? parsedMin : 0;
      }

      value = Math.min(
        Number.isFinite(parsedMax) ? parsedMax : Infinity,
        Math.max(
          Number.isFinite(parsedMin) ? parsedMin : -Infinity,
          parseFloat(value.toFixed(decimals))
        )
      );
      onDrag?.({ value, first: false, last: false, type: 'drag' });
    }
  });

  const onStart = useEventCallback<React.PointerEventHandler<TElement>>((evt) => {
    if (stopPropagation) {
      evt.stopPropagation();
    }
    if (disabled) return;
    setDragging(true);
    setStartVal(evt[axisVar]);
    setSnapshot(value);
    onDrag?.({ value, first: true, last: false, type: 'dragstart' });
  });

  const onEnd = useEventCallback((evt: PointerEvent) => {
    if (stopPropagation) {
      evt.stopPropagation();
    }
    setStartVal(0);
    onDrag?.({ value, first: false, last: true, type: 'dragend' });
    setDragging(false);
  });

  useLayoutEffect(() => {
    if (dragging) {
      document.addEventListener('pointermove', onUpdate);
      document.addEventListener('pointerup', onEnd);
    }
    return () => {
      document.removeEventListener('pointermove', onUpdate);
      document.removeEventListener('pointerup', onEnd);
    };
  }, [dragging, onEnd, onUpdate]);

  const cursor = disabled ? 'not-allowed' : axis === 'x' ? 'ew-resize' : 'ns-resize';

  return {
    onPointerDown: onStart,
    style: {
      cursor,
      userSelect: 'none',
    },
  } as React.DOMAttributes<TElement> & {
    style: React.CSSProperties;
  };
};
